import { Box, Stack, Typography } from "@mui/material"
import { ReactElement, useContext, useEffect, useMemo, useRef } from "react"

import useMultiFetcherGenFilterReferenceData from "../../../../../services/middleware/hooks/useMultiFetcherGenFilterReference"
import { GRID_SPACING } from "../../../../../themes/defaultThemeParams"
import GenDeviceSearchPanel from "../../../../deviceSearch/panels/genDeviceSearchPanel"
import FormHeader from "../../../../formHeader/formHeader"
import FormNavigation from "../../../../formNavigation/formNavigation"
import { AppCard, AppCardContent } from "../../../../surfaces/appCard"
import { IDatabaseSearchResult } from "../../../../tables/ttr/genSearchResults"
import { CreateV2GBundleContext } from "../providers/baseProviders/createV2GBundleProvider"

export const ExistingInverterSelectionFields = (): ReactElement => {
    const { selectedInverter, setSelectedInverter } = useContext(
        CreateV2GBundleContext
    )

    const { referenceData } = useMultiFetcherGenFilterReferenceData(true)

    const selectedInverterRef = useRef<HTMLDivElement | null>(null)

    const inverterCategoryId = useMemo(() => {
        const match = referenceData?.deviceCategories?.find(
            (category: { DeviceCategory: string }) =>
                category.DeviceCategory.toLowerCase() === "inverter"
        )
        return match?.DeviceCategoryID
    }, [referenceData])

    useEffect(() => {
        if (selectedInverter) {
            selectedInverterRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            })
        }
    }, [selectedInverter])

    return (
        <Stack spacing={GRID_SPACING * 2}>
            <Typography color="text.secondary">
                The search is restricted to generation devices in the Inverter
                category.
            </Typography>

            <GenDeviceSearchPanel
                locked={
                    inverterCategoryId !== undefined
                        ? { categoryId: inverterCategoryId }
                        : undefined
                }
                onSelect={(row: IDatabaseSearchResult) =>
                    setSelectedInverter(row)
                }
                selectedDatabaseId={selectedInverter?.databaseId ?? null}
                forcePublicRoute
            />

            <Box
                ref={selectedInverterRef}
                sx={{
                    border: "1px solid",
                    borderColor: selectedInverter ? "primary.main" : "divider",
                    borderRadius: 2,
                    px: 2,
                    py: 1.5,
                    backgroundColor: selectedInverter
                        ? "action.selected"
                        : "background.default",
                }}
            >
                <Stack spacing={0.5}>
                    <Typography
                        variant="overline"
                        color={
                            selectedInverter ? "primary.main" : "text.secondary"
                        }
                    >
                        {selectedInverter
                            ? "Selected inverter"
                            : "Inverter selection"}
                    </Typography>
                    <Typography variant="h6" color="text.primary">
                        {selectedInverter
                            ? `${selectedInverter.manufacturer} ${selectedInverter.model}`
                            : "No inverter selected yet"}
                    </Typography>
                    {selectedInverter && (
                        <Typography variant="body2" color="text.secondary">
                            System reference: {selectedInverter.deviceReference}
                        </Typography>
                    )}
                </Stack>
            </Box>
        </Stack>
    )
}

const SelectExistingInverter = (): ReactElement => {
    const { currentIndex, setCurrentIndex, selectedInverter } = useContext(
        CreateV2GBundleContext
    )

    return (
        <AppCard>
            <Stack spacing={GRID_SPACING * 2}>
                <FormHeader
                    header="Select Existing Inverter"
                    subHeader="Search the generation register for an existing inverter to link to this V2G bundle"
                    index={currentIndex + 1}
                />
                <AppCardContent>
                    <ExistingInverterSelectionFields />
                </AppCardContent>
                <FormNavigation
                    onClickBack={() => setCurrentIndex(currentIndex - 1)}
                    onClickNext={() => setCurrentIndex(currentIndex + 1)}
                    nextDisabled={!selectedInverter}
                />
            </Stack>
        </AppCard>
    )
}

export default SelectExistingInverter
